// Budget demo · domain types
//
// One source of truth for the demo's data shape. The budget lifecycle FSM
// (lifecycle.ts) is the canonical "what state is this budget at"; the segment
// of a line is derived per (contract, year) by forecast.ts — never stored on
// the contract. Everything here is the persistent record.

import { BudgetState } from "./lifecycle";

type Brand<K, T> = K & { readonly __brand: T };

export type VendorId = Brand<string, "VendorId">;
export type ContractId = Brand<string, "ContractId">;
export type CategoryId = Brand<string, "CategoryId">;
export type BudgetLineId = Brand<string, "BudgetLineId">;
export type SourceId = Brand<string, "SourceId">;

export const vendorId = (s: string) => s as VendorId;
export const contractId = (s: string) => s as ContractId;
export const categoryId = (s: string) => s as CategoryId;
export const budgetLineId = (s: string) => s as BudgetLineId;
export const sourceId = (s: string) => s as SourceId;

export type CurrencyCode = "USD" | "EUR" | "GBP";
export interface Money {
    amount: number;
    currency: CurrencyCode;
}
export const usd = (amount: number): Money => ({ amount, currency: "USD" });

export type Tone = "green" | "teal" | "amber" | "red" | "blue" | "slate";

// ---------------------------------------------------------------------------
// Categories & vendors
// ---------------------------------------------------------------------------
export type CategoryKey =
    | "saas"
    | "infra_cloud"
    | "hardware"
    | "telecom"
    | "prof_services"
    | "security"
    | "data_analytics"
    | "support_maint";

export interface Category {
    id: CategoryId;
    key: CategoryKey;
    name: string;
}

export type VendorGrade = "A" | "A-" | "B+" | "B" | "B-" | "C+" | "C" | "C-";

export interface Vendor {
    id: VendorId;
    name: string;
    categoryId: CategoryId;
    grade?: VendorGrade;
}

// ---------------------------------------------------------------------------
// Contract — spec §2 minimum fields
// ---------------------------------------------------------------------------
export type CapBasis = "CPI" | "fixed_pct" | "step" | "none";
export type PricingType = "fixed" | "usage" | "seat_based";
export type ContractSource = "manual" | "connector";

export interface Contract {
    id: ContractId;
    vendorId: VendorId;
    vendorName: string;
    categoryId: CategoryId;
    categoryName: string;
    annualSpend: Money;
    /** Human labels — "Jan 1, 2024" etc. Dates are display-only in the demo. */
    startDate: string;
    endDate: string;
    renewalDate: string;
    autoRenew: boolean;
    noticePeriodDays: number;
    increaseCapped: boolean;
    capBasis: CapBasis;
    pricingType: PricingType;
    /** e.g. "CPI, capped 4%" or "Fixed 3%/yr step". */
    escalatorClause: string;
    /** Applied escalator as a fraction (0.04 = 4%); drives under-contract forecast. */
    escalatorPct: number;
    source: ContractSource;
    /** Above the materiality threshold → individually modeled. Cached; the
     * segment itself is still derived per year by forecast.ts. */
    material: boolean;
    /** Brand domain (e.g. "salesforce.com") — drives the logo.dev image. */
    domain?: string;
}

// ---------------------------------------------------------------------------
// Budget lines & budget
// ---------------------------------------------------------------------------
export type Segment =
    | "under_contract"
    | "up_for_renewal"
    | "tail"
    | "new_spend";

export type Method =
    | "contracted_escalator"
    | "renewal_range"
    | "tail_trend"
    | "initiative";

export type Confidence = "high" | "medium" | "low";

/** What kind of FY increase a line carries (drives the "Increase" column). */
export type IncreaseKind = "capped" | "market" | "usage";

export interface ForecastRange {
    worst: Money;
    expected: Money;
    floor: Money;
}

// ---------------------------------------------------------------------------
// Renewal negotiation playbook — backs the "Mitigation" column + drawer.
// ---------------------------------------------------------------------------
export interface CommercialStrategy {
    name: string;
    detail: string;
    /** Expected savings this strategy contributes. */
    savings: Money;
}

export interface RenewalPlaybook {
    /** Contract data box. */
    tcv: Money;
    termLabel: string;
    paymentTermsLabel: string;
    autoRenew: boolean;
    noticeDays: number;
    /** Score + contractual priorities box. */
    leverageScore: number; // 0–100
    priorities: string[];
    /** Identified savings box — range + the strategies that make it up. */
    savingsLow: Money;
    savingsExpected: Money;
    savingsHigh: Money;
    strategies: CommercialStrategy[];
}

export interface BudgetLine {
    id: BudgetLineId;
    year: number;
    segment: Segment;
    method: Method;
    /** Vendor name, "Tail · {category}", or initiative name. */
    label: string;
    categoryId: CategoryId;
    categoryName: string;
    contractId?: ContractId;
    /** Set when the line is backed by a real negotiation project — deep-links to
     * `/project/:projectId`. */
    projectId?: number;
    /** Vendor logo (logo.dev) — material contract lines only. */
    logoUrl?: string;
    /** Contract term (display labels) — material lines only. */
    startDate?: string;
    endDate?: string;
    /** Fiscal quarter the renewal closes (renewals) or the new spend is expected
     * to land (new-spend lines) — e.g. "Q3 2027". Shown instead of term dates
     * for the up-for-renewal and new-spend segments. */
    closeQuarter?: string;
    /** Whether the renewal increase is capped — drives confidence + the cap
     * pill. */
    capped?: boolean;
    /** Savings playbook — material contract lines (renewal + under contract). */
    playbook?: RenewalPlaybook;
    /** Current contracted annual spend (pre-forecast) — material lines. Drives
     * the forecast-year "current vs forecast" view. */
    currentSpend?: number;
    /** FY increase that drives the forecast, as a fraction (0.09 = 9%): the cap
     * (capped renewal), the market prediction (uncapped renewal), or planned
     * usage growth (under contract). See increaseKind. */
    increasePct?: number;
    increaseKind?: IncreaseKind;
    /** Stored confidence — new-spend lines only (editable). Other segments
     * derive it from their segment/cap status. */
    confidence?: Confidence;
    /** The budget number: "expected" for renewals, the escalated point
     * otherwise. */
    planAmount: Money;
    /** Posted actuals for elapsed quarters — current year only. */
    actualAmount?: Money;
    /** worst / expected / floor — renewals (and new-spend) carry a range. */
    range?: ForecastRange;
    /** The clause, benchmark, or trend that defends this line. */
    rationale: string;
    owner: string;
    /** Plan dollars phased across [Q1, Q2, Q3, Q4]. Sums to planAmount. */
    phasing: [number, number, number, number];
    /** Actuals posted per quarter — only elapsed quarters are present. */
    actualPhasing?: number[];
    /** Pre-rendered segment pill for tables (like tail-spend's statusChip). */
    segmentTag: { label: string; tone: Tone };
}

export interface Budget {
    year: number;
    state: BudgetState;
    lines: BudgetLine[];
}

// ---------------------------------------------------------------------------
// Data sources & ingest log
// ---------------------------------------------------------------------------
export type SourceKind = "po_feed" | "contract_upload" | "ap_feed" | "project";
export type SourceStatus = "connected" | "available" | "syncing";

export interface DataSource {
    id: SourceId;
    kind: SourceKind;
    system: string;
    name: string;
    detail: string;
    /** Live connector (continuously synced) vs. batch upload (one-off imports).
     * Batch sources have no connection status — only an "uploaded" history. */
    live: boolean;
    status: SourceStatus;
    logoLabel: string;
    logoColor: string;
    lastSyncedLabel: string;
    contractsIngested: number;
    spendDollarsCovered: Money;
}

// ---------------------------------------------------------------------------
// Workspace — the in-memory "database"
// ---------------------------------------------------------------------------
export interface Workspace {
    org: { id: string; name: string; unit: string };
    currentUser: { id: string; name: string; role: string };
    /** Current fiscal year being tracked. Forecast year = currentYear + 1. */
    currentYear: number;
    /** $-threshold below which a contract is treated as tail regardless of
     * status (spec §4 materiality). */
    materialityThreshold: Money;
    categories: Category[];
    vendors: Vendor[];
    contracts: Contract[];
    budgets: Budget[];
    sources: DataSource[];
    /** Contracts brought in via batch upload (the "Contract upload" source). */
    uploadedContracts: Contract[];
}
